import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, HelpCircle, Loader2, RefreshCw, FileText, Hash, Ban } from 'lucide-react';

const RE_N2 = /ANALISE\s*N2|FATURA\s+DA\s+CONCORR/i;
// Reprovado "análise N2 com fatura da concorrência": a Claro não libera nenhum produto → nenhum plano e a venda é cancelada automaticamente
export const ehAnaliseN2 = (r) => !!r && r.status === 'ok' && r.resultado !== 'APROVADO' && (r.analise_n2 === true || RE_N2.test(r.mensagem || ''));
export const liberaPlanos = (r) => !r || r.status !== 'ok' || r.aprovado;
export const AVISO_N2 = 'A Claro não libera nenhum produto para este cliente (análise N2 — fatura da concorrência). A venda é cancelada automaticamente.';

export const fmtDoc = (d) => {
  const n = (d || '').replace(/\D/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
  if (n.length === 14) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
  return d || '';
};
export const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const fmtLimite = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null;
};

export const situacao = (resultado, mensagem = '') => {
  if (resultado === 'APROVADO') return { label: 'APROVADO', tom: 'verde', Icone: CheckCircle2 };
  if (RE_N2.test(mensagem || '')) return { label: 'REPROVADO — ANÁLISE N2 (FATURA DA CONCORRÊNCIA)', curto: 'REPROVADO N2', tom: 'vermelho', Icone: Ban, n2: true };
  if (resultado === 'NAO_APROVADO' || resultado === 'REPROVADO') return { label: 'REPROVADO', tom: 'vermelho', Icone: XCircle };
  return { label: (resultado || 'SEM RESULTADO').replace(/_/g, ' '), tom: 'ambar', Icone: HelpCircle };
};
const TONS = {
  verde: { badge: 'bg-emerald-600 hover:bg-emerald-600', caixa: 'border-emerald-400 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20', texto: 'text-emerald-800 dark:text-emerald-200' },
  vermelho: { badge: 'bg-red-600 hover:bg-red-600', caixa: 'border-red-400 dark:border-red-800 bg-red-50 dark:bg-red-900/20', texto: 'text-red-800 dark:text-red-200' },
  ambar: { badge: 'bg-amber-500 hover:bg-amber-500', caixa: 'border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20', texto: 'text-amber-800 dark:text-amber-200' },
};

export const CreditoBadge = ({ resultado, mensagem, className = '', testid }) => {
  const s = situacao(resultado, mensagem);
  return <Badge className={`gap-1 ${TONS[s.tom].badge} ${className}`} title={s.n2 ? s.label : undefined} data-testid={testid}><s.Icone className="w-3 h-3" /> {s.curto || s.label}</Badge>;
};

const Assinantes = ({ lista }) => (
  <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden" data-testid="credito-assinantes">
    <div className="px-3 py-2 border-b bg-gray-50 dark:bg-gray-800/60 text-xs font-semibold flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Contratos existentes na Claro ({lista.length})</div>
    <table className="w-full text-xs">
      <thead><tr className="text-left text-gray-500 border-b"><th className="px-3 py-1.5 font-medium">Contrato</th><th className="px-3 py-1.5 font-medium">Nome</th><th className="px-3 py-1.5 font-medium">Situação</th><th className="px-3 py-1.5 font-medium hidden sm:table-cell">HP</th></tr></thead>
      <tbody>
        {lista.map((a, i) => (
          <tr key={`${a.contrato}-${i}`} className="border-b last:border-0" data-testid={`credito-assinante-${a.contrato}`}>
            <td className="px-3 py-1.5 font-mono">{a.contrato}</td>
            <td className="px-3 py-1.5">{a.nome}</td>
            <td className={`px-3 py-1.5 font-semibold ${/ATIV|CONECT/i.test(a.status) && !/DESCONECT/i.test(a.status) ? 'text-emerald-600' : 'text-gray-500'}`}>{a.status}</td>
            <td className="px-3 py-1.5 hidden sm:table-cell font-mono text-gray-500">{a.hp}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CreditoResultado = ({ r, onNovamente, consultando }) => {
  const s = situacao(r.resultado, r.mensagem);
  const t = TONS[s.tom];
  const limite = fmtLimite(r.limite);
  return (
    <div className={`rounded-xl border-2 p-4 space-y-3 ${t.caixa}`} data-testid="credito-resultado">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={`inline-flex items-center gap-2 text-lg font-bold ${t.texto}`} data-testid="credito-situacao"><s.Icone className="w-6 h-6" /> {s.label}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {limite && parseFloat(r.limite) > 0 && <Badge variant="outline" className="bg-white/70 dark:bg-black/20">Limite {limite}</Badge>}
          {r.policy && <Badge variant="outline" className="bg-white/70 dark:bg-black/20">Política {r.policy}</Badge>}
          {r.consulta_credito_id && <Badge variant="outline" className="bg-white/70 dark:bg-black/20 gap-1 font-mono" data-testid="credito-id-claro"><Hash className="w-3 h-3" />{r.consulta_credito_id}</Badge>}
        </div>
      </div>
      {r.mensagem && <p className={`text-sm font-semibold ${t.texto}`} data-testid="credito-mensagem">{r.mensagem}</p>}
      {s.n2 && <p className="text-xs font-semibold text-red-800 dark:text-red-200" data-testid="credito-n2-aviso">{AVISO_N2}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs rounded-lg bg-white/70 dark:bg-black/20 p-3">
        <div><span className="text-gray-500 block">Cliente</span><span className="font-medium">{r.nome}</span></div>
        <div><span className="text-gray-500 block">{r.tipo_documento}</span><span className="font-mono font-medium" data-testid="credito-doc">{fmtDoc(r.cpf_cnpj)}</span></div>
        <div><span className="text-gray-500 block">Tipo</span><span className="font-medium">{r.tipo_venda === 'BASE' ? `Cliente da base • contrato ${r.numero_contrato}` : 'Novo cliente (Prospect)'} • {r.tipo_cliente === 'EMPRESARIAL' ? 'Empresarial' : 'Residencial'}</span></div>
        <div><span className="text-gray-500 block">Cidade</span><span className="font-medium">{r.cidade}/{r.uf}</span></div>
        <div><span className="text-gray-500 block">CEP</span><span className="font-medium">{r.cep}</span></div>
        {r.data_nascimento && <div><span className="text-gray-500 block">Nascimento</span><span className="font-medium">{r.data_nascimento}</span></div>}
      </div>
      {r.assinantes?.length > 0 && <Assinantes lista={r.assinantes} />}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-gray-400">
        <span>Consultado em {fmtDataHora(r.respondida_em || r.criado_em)} por {r.criado_por} • via conector do navegador{r.reaproveitada ? ' • resultado recente reaproveitado' : ''}</span>
        {onNovamente && (
          <Button size="sm" variant="outline" className="h-7" onClick={onNovamente} disabled={consultando} data-testid="credito-consultar-novamente">
            {consultando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Consultar novamente
          </Button>
        )}
      </div>
    </div>
  );
};
